import type { ContentType } from "@brookbush/database";

import { forwardRef, memo } from "react";
import { useInView } from "react-intersection-observer";

import Comment from "./Comment";
import CommentEditor from "./CommentEditor";
import Skeleton from "./Skeleton";
import { useComments } from "~/hooks";

interface CommentsProps {
	contentId: string;
	contentType: ContentType;
	id?: string;
}

const Comments = forwardRef<HTMLDivElement, CommentsProps>((props) => {
	const { ref, inView } = useInView({
		triggerOnce: true,
	});

	const {
		comments,
		addComment,
		setSize,
		size,
		hasMore,
		updateComment,
		removeComment,
	} = useComments(inView ? props : undefined);

	return (
		<div
			ref={ref}
			className="col-span-full laptop:col-span-7 flex w-full max-w-screen-md scroll-m-24 flex-col gap-4 phablet:gap-6"
			id="comments"
		>
			<h3 className="font-semibold phablet:text-2xl text-xl">Comments</h3>
			<div id={props.id} className="scroll-m-44">
				<CommentEditor
					success={{
						label: "Comment",
						onClick: addComment,
					}}
				/>
			</div>
			{inView && (
				<div>
					{!comments ? (
						<div className="flex flex-col gap-6">
							{Array(5)
								.fill({})
								.map((_, i) => (
									<Skeleton
										key={i}
										className="!rounded-tl-none !rounded-xl h-[92px] w-64 sm:w-96"
									/>
								))}
						</div>
					) : (
						!!comments?.length && (
							<div className="flex flex-col gap-6">
								{comments.map((comment) => (
									<Comment
										comment={comment}
										addComment={addComment}
										updateComment={updateComment}
										removeComment={removeComment}
										key={comment.id}
										{...props}
									/>
								))}
							</div>
						)
					)}

					{hasMore && !!comments?.length && (
						<button
							onClick={() => setSize(size + 1)}
							className="mx-auto max-w-max rounded-full bg-slate-100 px-4 py-1.5 font-medium hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600"
						>
							Load more comments
						</button>
					)}
				</div>
			)}
		</div>
	);
});

Comments.displayName = "Comments";

export default memo(Comments);
